import { useMutation, useQuery } from '@apollo/client'
import { Paper, Typography } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { Formik } from 'formik'
import React, { useMemo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'

import PageHeader from '../../components/PageHeader'
import {
  CREATE_TICKET_MUTATION,
  TICKET_CONTEXT_CAPABILITIES_QUERY
} from '../../graphql/tickets'
import { buildDiagnosticTraceInput } from '../../helpers/diagnosticTrace'
import {
  SupportContextCapabilities,
  supportContextReadiness
} from '../../helpers/supportCapabilities'
import {
  clearSupportDraft,
  recoverSupportDraft
} from '../../helpers/supportDraft'
import {
  SUPPORT_PAGE_LABELS,
  validatedSupportPageContext
} from '../../helpers/supportLaunchContext'
import { Ticket } from '../../helpers/ticketWorkflow'
import {
  findEntityTypeFromRole,
  getEmailFromStorage,
  getEntityCode,
  getRoleFromStorage
} from '../../helpers/userData'
import { getTranslate, lang } from '../../localization'
import { ROLE } from '../../staticRoleManagement/types'
import SupportTicketCreateForm from './SupportTicketCreateForm'
import SupportTicketDraftSaver from './SupportTicketDraftSaver'
import { SupportTicketDraft } from './types'
import { buildSupportTicketCreateValidationSchema } from './validations/supportTicketCreateValidation'

/**
 * Open a support ticket.
 *
 * Reporter entity is explicit. Admins may preserve the customer's reporter
 * identity while the service records the admin separately as the creator.
 *
 * Composition and data only (§8.4) — the fields live in
 * SupportTicketCreateForm and the rules in validations/.
 */
export default function SupportTicketCreate() {
  const history = useHistory()
  const isAdmin = getRoleFromStorage() === ROLE.ADMIN
  const capabilities = useQuery<{
    ticketContextCapabilities: SupportContextCapabilities
  }>(TICKET_CONTEXT_CAPABILITIES_QUERY)
  const contextCapabilities =
    !capabilities.loading && !capabilities.error
      ? capabilities.data?.ticketContextCapabilities
      : undefined
  const location = useLocation<unknown>()
  const principalId = getEmailFromStorage?.() || ''
  const entryKey = location.key || 'default'
  const recovery = useMemo(
    () => recoverSupportDraft(principalId, entryKey, Date.now()),
    [principalId, entryKey]
  )
  const savedDraft = recovery.draft

  // One key per mounted form: a double-submit or a retried request returns the
  // ORIGINAL ticket instead of opening a second one. A fresh visit is genuinely
  // a new ticket and gets a new key.
  const idempotencyKey = useMemo(
    () =>
      savedDraft?.idempotencyKey ||
      `admin-panel-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
    [savedDraft, principalId, entryKey]
  )

  const [createTicket, { loading }] = useMutation<{ createTicket: Ticket }>(
    CREATE_TICKET_MUTATION
  )

  // Set when the reporter arrived via the floating "report a problem here"
  // button, absent when they navigated to this form themselves.
  const origin =
    savedDraft?.origin || validatedSupportPageContext(location.state)

  const initialValues: SupportTicketDraft = {
    title: '',
    // Only registered pages supply a default. The reporter may change it.
    productScope: origin?.productScope || '',
    failedOperation: origin?.failedOperation,
    affectedEntities: origin?.affectedEntities || [],
    relatedEntities: origin?.relatedEntities || [],
    relatedPromotions: origin?.relatedPromotions || [],
    relatedWalletTransactions: origin?.relatedWalletTransactions || [],
    relatedDistribution: origin?.relatedDistribution || [],
    confirmAffectedEntities: false,
    // Origin is structured metadata; description remains the reporter's words.
    body: '',
    reporterMode: 'CALLER',
    // Lower-cased, as the original did: the server's entity types are
    // lower-case and the submit path lower-cases again, so seeding the field
    // in the same shape means what the operator SEES matches what is sent.
    entityType: findEntityTypeFromRole(getRoleFromStorage()).toLowerCase(),
    entityCode: getEntityCode() || '',
    customerUserId: '',
    externalName: '',
    externalEmail: '',
    externalPhone: '',
    attachTrace: false,
    traceId: '',
    traceFrom: '',
    traceTo: '',
    traceServices: ''
  }

  function onSubmit(values: SupportTicketDraft) {
    const readiness = supportContextReadiness(
      values,
      contextCapabilities,
      Boolean(capabilities.loading)
    )
    if (
      readiness.checking ||
      readiness.affectedLimitExceeded ||
      readiness.contextLimitExceeded
    ) {
      return Promise.resolve()
    }
    const diagnosticTrace = buildDiagnosticTraceInput(
      values.attachTrace,
      values.traceId,
      values.traceFrom,
      values.traceTo,
      values.traceServices
    )
    const reporter = {
      mode: values.reporterMode,
      ...(values.entityType && values.entityCode
        ? {
            entityRef: {
              type: values.entityType.toLowerCase(),
              code: values.entityCode
            }
          }
        : {}),
      ...(values.reporterMode === 'CUSTOMER_USER'
        ? { customerUserId: values.customerUserId }
        : {}),
      ...(values.reporterMode === 'EXTERNAL'
        ? {
            externalName: values.externalName,
            ...(values.externalEmail
              ? { externalEmail: values.externalEmail }
              : { externalPhoneE164: values.externalPhone })
          }
        : {})
    }

    const relatedRecords = [
      ...(contextCapabilities?.enabledRefTypes.includes('WALLET_TRANSACTION')
        ? (values.relatedWalletTransactions || []).map(({ id }) => ({
            refType: 'WALLET_TRANSACTION',
            recordId: id
          }))
        : []),
      ...(contextCapabilities?.enabledRefTypes.includes('ASSET_ENTITY')
        ? values.relatedEntities.map(({ type, code }) => ({
            refType: 'ASSET_ENTITY',
            entityRef: { type, code }
          }))
        : []),
      ...(contextCapabilities?.enabledRefTypes.includes('PROMOTION')
        ? values.relatedPromotions.map(({ id }) => ({
            refType: 'PROMOTION',
            recordId: id
          }))
        : []),
      ...values.relatedDistribution
        .filter(({ refType }) =>
          contextCapabilities?.enabledRefTypes.includes(refType)
        )
        .map(({ refType, label: _label, ...distributionRef }) => ({
          refType,
          distributionRef
        }))
    ]
    return createTicket({
      variables: {
        input: {
          title: values.title,
          productScope: values.productScope,
          submissionChannel: 'WEB',
          body: values.body,
          idempotencyKey,
          reporter,
          affectedEntityRefs: values.affectedEntities.map(({ type, code }) => ({
            type,
            code
          })),
          ...(relatedRecords.length ? { relatedRecords } : {}),
          ...(origin ? { sourcePageKey: origin.pageKey } : {}),
          ...(diagnosticTrace ? { diagnosticTrace } : {})
        }
      }
    })
      .then((result) => {
        const created = result.data?.createTicket
        if (created) {
          clearSupportDraft(principalId)
          toast.success(getTranslate('Ticket created.'))
          history.push(`/${lang}/support/tickets/detail/${created.id}`)
        }
      })
      .catch((error) => toast.error(error.message))
  }

  return (
    <div>
      <PageHeader title={getTranslate('New ticket')} />
      <Paper style={{ padding: 16 }}>
        {recovery.expired && (
          <Alert severity="info" role="status">
            {getTranslate(
              'Your saved draft expired. Please enter the ticket details again.'
            )}
          </Alert>
        )}
        {origin?.routeTemplate && (
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginBottom: 16 }}>
            {getTranslate('Reporting from')}:{' '}
            {getTranslate(SUPPORT_PAGE_LABELS[origin.pageKey])}
          </Typography>
        )}
        <Formik
          key={`${principalId}:${entryKey}`}
          // Without this, `isValid` starts true on an untouched empty form and
          // the submit button would be enabled before anything was entered.
          validateOnMount
          initialValues={{ ...initialValues, ...savedDraft?.values }}
          validationSchema={buildSupportTicketCreateValidationSchema(isAdmin)}
          onSubmit={onSubmit}>
          {(formik) => (
            <>
              <SupportTicketDraftSaver
                principalId={principalId}
                entryKey={entryKey}
                idempotencyKey={idempotencyKey}
                origin={origin}
              />
              <SupportTicketCreateForm
                {...formik}
                isAdmin={isAdmin}
                contextCapabilities={contextCapabilities}
                contextCapabilitiesLoading={Boolean(capabilities.loading)}
                contextCapabilitiesUnavailable={Boolean(capabilities.error)}
                // Formik's isSubmitting covers a submit STARTED HERE; Apollo's
                // loading also covers one already in flight when the form
                // mounted or retried elsewhere. The button must reflect either.
                loading={loading}
                onCancel={() => history.push(`/${lang}/support`)}
              />
            </>
          )}
        </Formik>
      </Paper>
    </div>
  )
}
